import { useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { Send, Loader2, CheckCircle2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FIELDS = [
  { name: "name", label: "Your Name", placeholder: "Jane Doe", type: "text", required: true },
  { name: "phone", label: "Phone Number", placeholder: "(530) 000-0000", type: "tel", required: true },
  { name: "email", label: "Email", placeholder: "you@email.com", type: "email", required: true },
  { name: "pickup_time", label: "Pickup Date / Time", placeholder: "Sat, 6:00 PM", type: "text", required: false },
];

const empty = { name: "", phone: "", email: "", items: "", pickup_time: "", notes: "" };

const emailOk = (v) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v.trim());

export const OrderForm = () => {
  const [form, setForm] = useState(empty);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [ref, setRef] = useState("");

  const update = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    toast.dismiss();
    if (!form.name.trim() || !form.phone.trim() || !form.email.trim() || !form.items.trim()) {
      toast.error("Please add your name, phone, email and what you'd like to order.");
      return;
    }
    if (!emailOk(form.email)) {
      toast.error("Please enter a valid email so we can confirm your order.");
      return;
    }
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/orders`, form);
      setRef(data.ref || "");
      setDone(true);
      setForm(empty);
      toast.success(`Order request sent! Ref ${data.ref} — check your email.`);
    } catch (err) {
      if (err?.response?.status === 429) {
        toast.error(
          err.response.data?.detail ||
            "Too many requests. Please wait a few minutes or call us at (216) 250-1108."
        );
      } else {
        toast.error("Something went wrong. Please call us at (216) 250-1108.");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="order"
      data-testid="order-section"
      className="relative border-t border-white/10 bg-[#0a0a0a]"
    >
      <div className="mx-auto max-w-[1600px] px-5 md:px-10 py-24 md:py-40 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
          className="lg:col-span-5"
        >
          <p className="font-body uppercase tracking-[0.4em] text-[#ffb703] text-xs md:text-sm mb-6">
            Skip The Phone Tag
          </p>
          <h2 className="font-display uppercase leading-[0.85] tracking-tight text-6xl md:text-8xl">
            Request <span className="text-[#e63946]">An</span> Order
          </h2>
          <p className="mt-8 font-body text-base md:text-lg text-[#a8a8a8] leading-relaxed max-w-md">
            Tell us what you&apos;re craving and when you need it. We&apos;ll get your
            request instantly and call you back to lock it in.
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1], delay: 0.1 }}
          className="lg:col-span-7"
        >
          {done ? (
            <div
              data-testid="order-success"
              className="flex flex-col items-start gap-6 border border-[#ffb703]/40 bg-[#ffb703]/5 p-8 md:p-12 rounded-md"
            >
              <CheckCircle2 className="h-12 w-12 text-[#ffb703]" />
              <h3 className="font-display uppercase text-4xl md:text-5xl leading-none tracking-tight">
                Request Received!
              </h3>
              {ref && (
                <div
                  data-testid="order-ref-code"
                  className="w-full sm:w-auto border border-[#ffb703] rounded-md px-6 py-4 bg-[#0a0a0a]"
                >
                  <span className="block font-body uppercase tracking-widest text-[10px] text-[#a8a8a8] mb-1">
                    Your reference #
                  </span>
                  <span className="font-accent text-3xl md:text-4xl tracking-widest text-[#ffb703]">
                    {ref}
                  </span>
                </div>
              )}
              <p className="font-body text-[#a8a8a8] max-w-md">
                Thanks — we sent a confirmation to your email and will call you
                shortly to lock in the details. Keep your reference number handy.
              </p>
              <button
                data-testid="order-another-button"
                onClick={() => setDone(false)}
                className="font-body uppercase tracking-widest text-sm text-[#ffb703] hover-underline"
              >
                Send another request
              </button>
            </div>
          ) : (
            <form onSubmit={submit} noValidate data-testid="order-form" className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {FIELDS.map((f) => (
                <div key={f.name} className={f.name === "pickup_time" || f.name === "email" ? "md:col-span-2" : ""}>
                  <label htmlFor={`order-${f.name}`} className="block font-body uppercase tracking-widest text-xs text-[#a8a8a8] mb-2">
                    {f.label} {f.required && <span className="text-[#e63946]">*</span>}
                  </label>
                  <input
                    id={`order-${f.name}`}
                    data-testid={`order-input-${f.name}`}
                    name={f.name}
                    type={f.type}
                    value={form[f.name]}
                    onChange={update}
                    placeholder={f.placeholder}
                    className="w-full bg-[#141414] border border-white/10 rounded-md px-4 py-3.5 font-body text-white placeholder:text-white/25 outline-none transition-colors duration-300 focus:border-[#ffb703] focus:ring-2 focus:ring-[#ffb703]/30"
                  />
                </div>
              ))}

              <div className="md:col-span-2">
                <label htmlFor="order-items" className="block font-body uppercase tracking-widest text-xs text-[#a8a8a8] mb-2">
                  What Would You Like? <span className="text-[#e63946]">*</span>
                </label>
                <textarea
                  id="order-items"
                  data-testid="order-input-items"
                  name="items"
                  rows={3}
                  value={form.items}
                  onChange={update}
                  placeholder="e.g. 2x Spaghetti Dinner, 10 Chicken Wings, 1x Baked Beans"
                  className="w-full bg-[#141414] border border-white/10 rounded-md px-4 py-3.5 font-body text-white placeholder:text-white/25 outline-none transition-colors duration-300 focus:border-[#ffb703] focus:ring-2 focus:ring-[#ffb703]/30 resize-none"
                />
              </div>

              <div className="md:col-span-2">
                <label htmlFor="order-notes" className="block font-body uppercase tracking-widest text-xs text-[#a8a8a8] mb-2">
                  Notes (optional)
                </label>
                <textarea
                  id="order-notes"
                  data-testid="order-input-notes"
                  name="notes"
                  rows={2}
                  value={form.notes}
                  onChange={update}
                  placeholder="Allergies, extra sauce, delivery address…"
                  className="w-full bg-[#141414] border border-white/10 rounded-md px-4 py-3.5 font-body text-white placeholder:text-white/25 outline-none transition-colors duration-300 focus:border-[#ffb703] focus:ring-2 focus:ring-[#ffb703]/30 resize-none"
                />
              </div>

              <div className="md:col-span-2">
                <button
                  type="submit"
                  data-testid="order-submit-button"
                  disabled={loading}
                  className="group inline-flex items-center gap-3 rounded-full bg-[#e63946] px-8 py-4 font-accent uppercase tracking-wide text-white text-lg transition-all duration-300 hover:bg-[#ffb703] hover:text-black hover:scale-105 focus:outline-none focus:ring-2 focus:ring-[#ffb703] disabled:opacity-60 disabled:hover:scale-100 disabled:cursor-not-allowed"
                >
                  {loading ? (
                    <Loader2 className="h-5 w-5 animate-spin" />
                  ) : (
                    <Send className="h-5 w-5 transition-transform group-hover:translate-x-1" />
                  )}
                  {loading ? "Sending…" : "Send Order Request"}
                </button>
              </div>
            </form>
          )}
        </motion.div>
      </div>
    </section>
  );
};
